import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CAST_PER_PAGE } from './cast-content-constants';
import { ESortOrderMap } from '@ui/buttons/sort-button/types';
import { Speaker } from '@/types/speakers.types';
import { useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { EPermissions } from '@/types/permissions.types';
import getCastInviteStatus from './use-cast-select-invite-status';
import { selectGreenroomSessionMeetings } from '@/store/slices/project/selectors';
import useParamsOrFirstSessionUuid from '@/utils/use-params-or-first-session-uuid';

const useSortedAndPagedCastList = ({ speakers, page }: { speakers: Speaker[]; page?: number }) => {
  const [searchParams] = useSearchParams();
  const activeChannel = useAppSelector(getActiveChannel);
  const sessionUuid = useParamsOrFirstSessionUuid();
  const meetings = useAppSelector(selectGreenroomSessionMeetings);
  const sessionMeetingData = meetings?.[sessionUuid || ''] || null;

  const sortedCast = useMemo(() => {
    if (!activeChannel) return null;
    if (!speakers.length) return null;

    const sortBy = searchParams.get('sortCastBy');
    const _sortOrder = Number(searchParams.get('sortCastOrder'));
    const sortOrder = isNaN(_sortOrder) ? 0 : _sortOrder;
    return speakers.toSorted((a, b) => {
      const speakerNameA = `${a.first_name} ${a.last_name}`.trim();
      const speakerNameB = `${b.first_name} ${b.last_name}`.trim();

      if (!sortBy || !sortOrder || sortBy === 'name') {
        if (sortOrder === ESortOrderMap.desc) {
          return speakerNameB.toLowerCase().localeCompare(speakerNameA.toLowerCase());
        }
        return speakerNameA.toLowerCase().localeCompare(speakerNameB.toLowerCase());
      }

      if (sortBy === 'role') {
        const adminChannelsA = a.bl_admin?.channels?.[activeChannel]?.includes(EPermissions.Presenter)
          ? 'Presenter'
          : 'Speaker';
        const adminChannelsB = b.bl_admin?.channels?.[activeChannel]?.includes(EPermissions.Presenter)
          ? 'Presenter'
          : 'Speaker';
        if (sortOrder === ESortOrderMap.desc) {
          return adminChannelsB.toLowerCase().localeCompare(adminChannelsA.toLowerCase());
        }
        return adminChannelsA.toLowerCase().localeCompare(adminChannelsB.toLowerCase());
      }

      if (sortBy === 'status') {
        const statusA = getCastInviteStatus({ speaker: a, activeChannel, sessionMeetingData });
        const statusB = getCastInviteStatus({ speaker: b, activeChannel, sessionMeetingData });
        if (sortOrder === ESortOrderMap.desc) {
          return statusB.toLowerCase().localeCompare(statusA.toLowerCase());
        }
        return statusA.toLowerCase().localeCompare(statusB.toLowerCase());
      }

      if (sortBy === 'email') {
        if (!a.email) return 0;
        if (!b.email) return -1;
        if (sortOrder === ESortOrderMap.desc) {
          return (b?.email || '').toLowerCase().localeCompare(a?.email?.toLowerCase());
        }
        return (a?.email || '').toLowerCase().localeCompare(b?.email?.toLowerCase());
      }

      // if no sort order set just return by name so we have some sort of reasonable default
      return speakerNameA.toLowerCase().localeCompare(speakerNameB.toLowerCase());
    });
  }, [activeChannel, speakers, searchParams, sessionMeetingData]);

  const pagedCast = useMemo(() => {
    const startIdx = Math.max((page || 1) - 1, 0);
    return sortedCast?.slice(startIdx * CAST_PER_PAGE, startIdx * CAST_PER_PAGE + CAST_PER_PAGE);
  }, [sortedCast, page]);

  return {
    sortedCast,
    pagedCast,
  };
};

export default useSortedAndPagedCastList;
